import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import ToggleButton from "@material-ui/lab/ToggleButton";
import {
  Divider,
  makeStyles,
  Paper,
  Theme,
  Tooltip,
  withStyles,
} from "@material-ui/core";
import { Translate } from "react-admin";
import {
  Dispatch,
  memo,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from "react";
import BluetoothAudioIcon from "@material-ui/icons/BluetoothAudio";
import BluetoothDisabledIcon from "@material-ui/icons/BluetoothDisabled";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import classNames from "classnames";
import assets from "../assets";
import trailers from "../administration/trailers";
import ProLogsCollapse from "../common/transitions/ProLogsCollapse";
import useTheme from "@material-ui/styles/useTheme";
import { VehicleIgnitionStatus } from "../../common/data/service-proxies";
import PowerIcon from "@material-ui/icons/Power";
import PowerOffIcon from "@material-ui/icons/PowerOff";

export enum MapSummaryAnimationState {
  TrucksSelecting,
  TrucksSelected,
  TrucksDeselecting,
  TrailersSelected,
}

const SET_ANIMATION_STATE = "SET_ANIMATION_STATE";
interface AnimationStateChangedAction {
  type: string;
  payload: MapSummaryAnimationState;
}

const createSetAnimationStateChangedAction = (
  state: MapSummaryAnimationState
) => ({ type: SET_ANIMATION_STATE, payload: state });

export type SelectedDutyStatusGroup = VehicleIgnitionStatus | "all";

export type SelectedOnlineStatusGroup = "all" | "online" | "offline";

export type SelectedPluggedStatusGroup = "all" | "plugged" | "unplugged";

export type SelectedGroup =
  | SelectedDutyStatusGroup
  | SelectedOnlineStatusGroup
  | SelectedPluggedStatusGroup;

export type SelectedGroupChangedEventArgs = {
  dutyStatus: SelectedDutyStatusGroup;
  onlineStatus: SelectedOnlineStatusGroup;
  plugged?: SelectedPluggedStatusGroup;
  disconnected?: boolean;
};

interface MapSummaryCommonProps {
  theme: Theme;
  offDuty: number;
  driving: number;
  sleeper: number;
  connected: number;
  disconnected: number;
  plugged: number;
  disconnectedItems: number;
  unplugged: number;
  all: number;
  trailers: number;
  translate: Translate;
  onGroupChanged: (group: SelectedGroupChangedEventArgs) => void;
  initialGroups?: SelectedGroupChangedEventArgs;
}

interface VehicleTypeSelectorProps {
  animationStateDispatch: Dispatch<AnimationStateChangedAction>;
  trailersCount: number;
  trucksCount: number;
}

interface MapSummaryProps extends MapSummaryCommonProps {
  loading: boolean;
  onAnimationStateChange: (value: MapSummaryAnimationState) => void;
}

interface TrucksSummaryProps extends MapSummaryCommonProps {
  animationStateDispatch: Dispatch<AnimationStateChangedAction>;
  animationState: MapSummaryAnimationState;
}

const getStatusColor = (status: string, theme: Theme) => {
  switch (status) {
    case "OFF":
      return theme.palette.grey[900];
    case "ON":
      return theme.palette.info.main;
    case "D":
      return theme.palette.success.main;
    case "SB":
      return theme.palette.warning.main;
    default:
      return "auto";
  }
};

const useStyles = makeStyles((theme) => ({
  paper: {
    display: "flex",
    border: `1px solid ${theme.palette.divider}`,
    flexWrap: "nowrap",
  },
  divider: {
    margin: theme.spacing(1, 0.5),
  },
}));

const StyledToggleButtonGroup = withStyles((theme) => ({
  grouped: {
    margin: theme.spacing(0.5),
    border: "none",
    "&:not(:first-child)": {
      borderRadius: theme.shape.borderRadius,
    },
    "&:first-child": {
      borderRadius: theme.shape.borderRadius,
    },
  },
}))(ToggleButtonGroup);

const VehicleTypeSelector = ({
  animationStateDispatch,
  trailersCount,
  trucksCount,
}: VehicleTypeSelectorProps) => {
  const [value, setValue] = useState<string>("trucks");
  const theme = useTheme<Theme>();
  const classes = useStyles();
  return (
    <Paper
      style={{ backgroundColor: theme.palette.background.paper }}
      className={classNames("current-locations-map-summary", classes.paper)}
    >
      <StyledToggleButtonGroup
        size="small"
        exclusive
        orientation="horizontal"
        value={value}
      >
        <ToggleButton
          value="trailers"
          onClick={(e) => {
            setValue("trailers");
            animationStateDispatch(
              createSetAnimationStateChangedAction(
                MapSummaryAnimationState.TrucksDeselecting
              )
            );
            e.stopPropagation();
          }}
        >
          <div>
            <div className="duty-status">
              <trailers.icon />
            </div>
            <div>{trailersCount}</div>
          </div>
        </ToggleButton>
        <ToggleButton
          value="trucks"
          onClick={(e) => {
            setValue("trucks");
            animationStateDispatch(
              createSetAnimationStateChangedAction(
                MapSummaryAnimationState.TrucksSelecting
              )
            );
            e.stopPropagation();
          }}
        >
          <div>
            <div className="duty-status">
              <assets.icon />
            </div>
            <div>{trucksCount}</div>
          </div>
        </ToggleButton>
      </StyledToggleButtonGroup>
    </Paper>
  );
};

const TrucksSummary = ({ theme, ...props }: TrucksSummaryProps) => {
  const {
    offDuty,
    driving,
    sleeper,
    disconnected,
    connected,
    plugged,
    unplugged,
    disconnectedItems,
    translate,
    onGroupChanged,
    initialGroups = {
      dutyStatus: "all",
      onlineStatus: "all",
      plugged: undefined,
      disconnected: false,
    },
    animationState,
    animationStateDispatch,
  } = props;
  const classes = useStyles();

  const handleDutyStatusGroupChange = (
    _: React.MouseEvent<HTMLElement, MouseEvent>,
    value: any
  ) => {
    onGroupChanged({
      ...initialGroups,
      dutyStatus: value?.length ? value : "all",
      disconnected: false,
    });
  };

  const handleOnlineStatusGroupChange = (
    _: React.MouseEvent<HTMLElement, MouseEvent>,
    value: any
  ) => {
    onGroupChanged({
      ...initialGroups,
      onlineStatus: value?.length ? value : "all",
      disconnected: false,
    });
  };

  const handlePluggedChange = (
    _: React.MouseEvent<HTMLElement, MouseEvent>,
    value?: SelectedPluggedStatusGroup
  ) => {
    onGroupChanged({
      ...initialGroups,
      plugged: value?.length ? value : "all",
      disconnected: false,
    });
  };

  const handleDisconnectedChange = (
    _: React.MouseEvent<HTMLElement, MouseEvent>,
    value?: boolean
  ) => {
    onGroupChanged({
      plugged: "all",
      onlineStatus: "all",
      dutyStatus: "all",
      disconnected: value,
    });
  };
  const handleTrucksSelectionAnimationCompleted = useCallback(() => {
    animationStateDispatch(
      createSetAnimationStateChangedAction(
        MapSummaryAnimationState.TrucksSelected
      )
    );
  }, [animationStateDispatch]);

  const handleTrucksDeselectionAnimationCompleted = useCallback(() => {
    animationStateDispatch(
      createSetAnimationStateChangedAction(
        MapSummaryAnimationState.TrailersSelected
      )
    );
  }, [animationStateDispatch]);

  const trucksClicked =
    animationState === MapSummaryAnimationState.TrucksSelecting ||
    animationState === MapSummaryAnimationState.TrucksSelected;

  return (
    <ProLogsCollapse
      in={trucksClicked}
      orientation="horizontal"
      onEntered={handleTrucksSelectionAnimationCompleted}
      onExited={handleTrucksDeselectionAnimationCompleted}
    >
      <Paper
        style={{ backgroundColor: theme.palette.background.paper }}
        className={
          animationState === MapSummaryAnimationState.TrucksSelected ||
            animationState === MapSummaryAnimationState.TrucksSelecting ||
            animationState === MapSummaryAnimationState.TrucksDeselecting
            ? classNames("current-locations-map-summary", classes.paper)
            : classes.paper
        }
      >
        <StyledToggleButtonGroup
          size="small"
          exclusive
          value={initialGroups.dutyStatus}
          onChange={handleDutyStatusGroupChange}
          orientation="horizontal"
        >
          <ToggleButton value={VehicleIgnitionStatus.IgnitionOnDriving}>
            <div>
              <Tooltip
                title={translate("appData.dashboardMapSummary.inMotion")}
              >
                <div
                  className="duty-status"
                  style={{
                    backgroundColor: getStatusColor("D", theme),
                    color: theme.palette.getContrastText(
                      getStatusColor("D", theme)
                    ),
                  }}
                >
                  DR
                </div>
              </Tooltip>
              <div>{driving}</div>
            </div>
          </ToggleButton>
          <ToggleButton value={VehicleIgnitionStatus.IgnitionOnIdle}>
            <div>
              <Tooltip title={translate("appData.dashboardMapSummary.idle")}>
                <div
                  className="duty-status"
                  style={{
                    backgroundColor: getStatusColor("SB", theme),
                    color: theme.palette.getContrastText(
                      getStatusColor("SB", theme)
                    ),
                  }}
                >
                  ID
                </div>
              </Tooltip>
              <div>{sleeper}</div>
            </div>
          </ToggleButton>
          <ToggleButton value={VehicleIgnitionStatus.IgnitionOff}>
            <div>
              <Tooltip
                title={translate("appData.dashboardMapSummary.engineOff")}
              >
                <div
                  className="duty-status"
                  style={{
                    backgroundColor: getStatusColor("OFF", theme),
                    color: theme.palette.getContrastText(
                      getStatusColor("OFF", theme)
                    ),
                  }}
                >
                  OF
                </div>
              </Tooltip>
              <div>{offDuty}</div>
            </div>
          </ToggleButton>
        </StyledToggleButtonGroup>
        <Divider flexItem orientation="vertical" className={classes.divider} />
        <StyledToggleButtonGroup
          size="small"
          exclusive
          value={initialGroups.onlineStatus}
          onChange={handleOnlineStatusGroupChange}
          orientation="horizontal"
        >
          <ToggleButton value="online">
            <div>
              <Tooltip
                title={translate(
                  "resources.locations-current.labels.connected"
                )}
              >
                <div className="duty-status">
                  <BluetoothAudioIcon
                    fontSize="small"
                    style={{ color: theme.palette.success.main }}
                  />
                </div>
              </Tooltip>
              <div>{connected}</div>
            </div>
          </ToggleButton>
          <ToggleButton value="offline">
            <div>
              <Tooltip
                title={translate(
                  "resources.locations-current.labels.disconnected"
                )}
              >
                <div className="duty-status">
                  <BluetoothDisabledIcon
                    fontSize="small"
                    style={{ color: theme.palette.error.main }}
                  />
                </div>
              </Tooltip>
              <div>{disconnected}</div>
            </div>
          </ToggleButton>
        </StyledToggleButtonGroup>
        <Divider flexItem orientation="vertical" className={classes.divider} />
        <StyledToggleButtonGroup
          size="small"
          exclusive
          value={initialGroups.plugged === undefined ? "all" : initialGroups.plugged}
          onChange={handlePluggedChange}
          orientation="horizontal"
        >
          <ToggleButton value="plugged">
            <div>
              <Tooltip
                title={translate("resources.locations-current.labels.plugged")}
              >
                <div className="duty-status">
                  <PowerIcon
                    fontSize="small"
                    style={{ color: theme.palette.success.main }}
                  />
                </div>
              </Tooltip>
              <div>{plugged}</div>
            </div>
          </ToggleButton>
          <ToggleButton value="unplugged">
            <div>
              <Tooltip
                title={translate(
                  "resources.locations-current.labels.unplugged"
                )}
              >
                <div className="duty-status">
                  <PowerOffIcon
                    fontSize="small"
                    style={{ color: theme.palette.error.main }}
                  />
                </div>
              </Tooltip>
              <div>{unplugged}</div>
            </div>
          </ToggleButton>
        </StyledToggleButtonGroup>
        <Divider flexItem orientation="vertical" className={classes.divider} />
        <StyledToggleButtonGroup
          size="small"
          exclusive
          value={!initialGroups.disconnected ? false : true}
          onChange={handleDisconnectedChange}
          orientation="horizontal"
        >
          <ToggleButton value={true}>
            <div>
              <Tooltip
                title={translate(
                  "resources.locations-current.labels.unidentifiedDriving"
                )}
              >
                <div className="duty-status">
                  <HelpOutlineIcon
                    fontSize="small"
                    style={{ color: theme.palette.error.main }}
                  />
                </div>
              </Tooltip>
              <div>{disconnectedItems}</div>
            </div>
          </ToggleButton>
        </StyledToggleButtonGroup>
      </Paper>
    </ProLogsCollapse>
  );
};

const animationStateReducer = (
  initialState = { animationState: MapSummaryAnimationState.TrucksSelected },
  action: AnimationStateChangedAction
) => {
  if (action.type !== SET_ANIMATION_STATE) {
    return initialState;
  }

  if (initialState.animationState === action.payload) {
    return initialState;
  }

  if (
    initialState.animationState === MapSummaryAnimationState.TrailersSelected
  ) {
    switch (action.payload) {
      case MapSummaryAnimationState.TrucksSelecting:
        return { animationState: action.payload };
      default:
        return initialState;
    }
  }

  if (initialState.animationState === MapSummaryAnimationState.TrucksSelected) {
    switch (action.payload) {
      case MapSummaryAnimationState.TrucksDeselecting:
        return { animationState: action.payload };
      default:
        return initialState;
    }
  }

  return { animationState: action.payload };
};

export const DashboardMapSummary = memo(
  ({ theme, onAnimationStateChange, loading, ...props }: MapSummaryProps) => {
    const [{ animationState }, dispatch] = useReducer(animationStateReducer, {
      animationState: MapSummaryAnimationState.TrucksSelected,
    });

    useEffect(() => {
      onAnimationStateChange(animationState);
    }, [animationState, onAnimationStateChange]);

    if (loading) {
      return null;
    }

    return (
      <div className="dashboard-map-summary">
        <VehicleTypeSelector
          animationStateDispatch={dispatch}
          trucksCount={props.all}
          trailersCount={props.trailers}
        />

        <div
          style={{
            display:
              animationState === MapSummaryAnimationState.TrucksSelected ||
                animationState === MapSummaryAnimationState.TrucksSelecting ||
                animationState === MapSummaryAnimationState.TrucksDeselecting
                ? "flex"
                : "none",
          }}
        >
          <TrucksSummary
            animationState={animationState}
            theme={theme}
            animationStateDispatch={dispatch}
            {...props}
          />
        </div>
        {/* <div
          style={{
            display:
              animationState === MapSummaryAnimationState.TrailersSelected
                ? "flex"
                : "none",
          }}
        >
          <TrailersSummary
            animationState={animationState}
            theme={theme}
            animationStateDispatch={dispatch}
            {...props}
          />
        </div> */}
      </div>
    );
  }
);
